/**
 * FALL RENDERER — stateless 2D renderer for the fall stage.
 *
 * No runway background, no perspective math. Each frame receives:
 *   - ctx, runWidth, runHeight
 *   - bodiesWithState: [{obj, body, suction}] from FallPhysics
 *   - player for flask visual position and shield state
 *   - currentTime for suction animation progress
   *   - state, countdownRemaining
 *
  * Draw order:
 *   1. Movement limit buoys in the sea (flask stop points)
 *   2. Player flask image (moves with `player.x`; no interior fill gauge)
 *   3. Fall collider debug overlay (`drawFallColliderDebug` when `runtimeFlags.showFallColliderDebug`)
 *   4. Live physics-positioned objects (regular + debris fragments), by body.position
 *   5. Orange circular shield glow (hex fill) while protected (appear/disappear scale+fade)
 *   6. Shard bursts, then countdown overlay, then the Fall callout (same anchor)
 *
 * The sky/sea background is drawn separately by the game host (`onDrawSky`), not here.
 */
import * as C from "../config/constants.js";
import { computeFallDrawTopOverflowPx, fallObjectPixelWidth } from "../config/constants.js";
import { fallCountdownFontSizePx, readMockupScaleSideFromDom } from "../layout/mockup-ui-scale.js";
import { FlaskRenderer } from "../flask/flask-renderer.js";
import { areRuntimeParticlesEnabled } from "../core/runtime-flags.js";
import { drawMergeParticles } from "./merge-particles.js";
import {
  drawFallMotionTrail,
  fallMotionTrailIntensity,
  getFallMotionTrailTint,
  getFallMotionTrailWidthMul,
  isFallMotionTrailEnabled,
  layoutFallMotionBlurGhosts,
} from "../render/fall-motion-trail.js";
import { prefersReducedMotion } from "../core/sky-background-prefs.js";
import { drawFallMovementRange, collectFallBuoySpriteSpecs } from "./fall-movement-range.js";
import { drawFallColliderDebug } from "./fall-collect-slab.js";
import {
  FALL_CALLOUT_ANCHOR_Y,
  computeFallCalloutFontPx,
  getFallCalloutPresentation,
} from "./fall-callouts.js";
import { computeScorePopupStrokeWidth } from "./score-popups.js";

/**
 * GPU sprite size matching each object's Canvas 2D `draw()` (gem square, shield letterbox).
 * @param {{ getType?: function, getAspectRatio?: function }} obj
 * @param {number} pixelW
 * @param {number} pixelH
 * @returns {{ width: number, height: number }}
 */
export function fallObjectSpriteSize(obj, pixelW, pixelH) {
  var w = pixelW > 0 ? pixelW : 0;
  var h = pixelH > 0 ? pixelH : 0;
  var type = obj && typeof obj.getType === "function" ? obj.getType() : "";
  if (type === "gem") {
    var size = Math.min(w, h);
    return { width: size, height: size };
  }
  if (type === "bonus") {
    var aspect = obj && typeof obj.getAspectRatio === "function" ? obj.getAspectRatio() : 1;
    if (!(aspect > 0)) aspect = 1;
    var drawW = w;
    var drawH = w / aspect;
    if (drawH > h) {
      drawH = h;
      drawW = h * aspect;
    }
    return { width: drawW, height: drawH };
  }
  return { width: w, height: h };
}

export function FallRenderer(runConfig, scale, mockupScale) {
    this.runConfig = runConfig || {};
    this.scale = scale || null;
    this.mockupScale = mockupScale || null;
  }

  FallRenderer.prototype._getFlaskDims = function (runHeight) {
    var scale = this.scale;
    var flaskW = scale && scale.runwayFlaskDisplayWidth ? scale.runwayFlaskDisplayWidth : 120;
    var flaskH = scale && scale.runwayFlaskDisplayHeight ? scale.runwayFlaskDisplayHeight : 120;
    var topFromBottom = C.FLASK_TOP_FROM_BOTTOM_RATIO != null
      ? C.FLASK_TOP_FROM_BOTTOM_RATIO : 0.24;
    var desiredTopY = runHeight * (1 - topFromBottom);
    var bottomGap = runHeight * 0.018;
    var flaskTopY = Math.min(desiredTopY, runHeight - flaskH - bottomGap);
    if (flaskTopY < 0) flaskTopY = 0;
    return { flaskW: flaskW, flaskH: flaskH, flaskTopY: flaskTopY };
  };

  /**
   * drawObjects — draw all physics bodies at their current position.
   * For sucked objects, apply fade-shrink animation based on elapsed suction time.
   */
  FallRenderer.prototype._shouldDrawMotionTrail = function (entry, currentTime, runHeight) {
    if (!entry || entry.suction) return false;
    if (!isFallMotionTrailEnabled(this.runConfig, { forceReducedMotion: prefersReducedMotion() })) {
      return false;
    }
    var appearMs = this.runConfig.FALL_SPAWN_APPEAR_MS != null
      ? Number(this.runConfig.FALL_SPAWN_APPEAR_MS)
      : (C.RUN_CONFIG && C.RUN_CONFIG.FALL_SPAWN_APPEAR_MS != null
        ? Number(C.RUN_CONFIG.FALL_SPAWN_APPEAR_MS)
        : 260);
    if (entry.spawnTime != null && appearMs > 0 && currentTime != null) {
      if ((currentTime - entry.spawnTime) < appearMs * 0.55) return false;
    }
    var body = entry.body;
    var vx = body && body.velocity && isFinite(body.velocity.x) ? body.velocity.x : 0;
    var vy = body && body.velocity && isFinite(body.velocity.y) ? body.velocity.y : 0;
    return fallMotionTrailIntensity(vx, vy, this.runConfig, runHeight) > 0;
  };

  FallRenderer.prototype._drawObjectMotionTrail = function (ctx, entry, px, py, pixelW, pixelH, type, runHeight) {
    var body = entry.body;
    var vx = body && body.velocity && isFinite(body.velocity.x) ? body.velocity.x : 0;
    var vy = body && body.velocity && isFinite(body.velocity.y) ? body.velocity.y : 0;
    var intensity = fallMotionTrailIntensity(vx, vy, this.runConfig, runHeight);
    if (intensity <= 0) return;
    var size = Math.max(pixelW, pixelH);
    var cfg = this.runConfig || {};
    var lengthMul = cfg.FALL_MOTION_TRAIL_LENGTH_MUL != null
      ? Number(cfg.FALL_MOTION_TRAIL_LENGTH_MUL)
      : (C.RUN_CONFIG && C.RUN_CONFIG.FALL_MOTION_TRAIL_LENGTH_MUL != null
        ? Number(C.RUN_CONFIG.FALL_MOTION_TRAIL_LENGTH_MUL)
        : 0.45);
    if (!isFinite(lengthMul) || lengthMul <= 0) lengthMul = 0.16;
    drawFallMotionTrail(ctx, px, py, size, {
      velocity: { x: vx, y: vy },
      trailScale: intensity,
      depth: 0.42 + intensity * 0.5,
      prominence: 1.35,
      tintRgb: getFallMotionTrailTint(type),
      trailWidth: pixelW * getFallMotionTrailWidthMul(type),
      lengthMul: lengthMul,
    });
  };

  FallRenderer.prototype._publishObjectMotionTrail = function (opts, entry, px, py, pixelW, pixelH, drawAlpha, drawScale, angle, runHeight) {
    var playfield = opts && opts.playfield;
    if (!playfield || typeof playfield.sprite !== "function") return false;
    var obj = entry.obj;
    var img = obj && typeof obj.getDrawImage === "function" ? obj.getDrawImage() : null;
    if (!img) return true;
    var body = entry.body;
    var vx = body && body.velocity && isFinite(body.velocity.x) ? body.velocity.x : 0;
    var vy = body && body.velocity && isFinite(body.velocity.y) ? body.velocity.y : 0;
    var intensity = fallMotionTrailIntensity(vx, vy, this.runConfig, runHeight);
    if (intensity <= 0) return true;
    var s = drawScale != null && isFinite(drawScale) ? drawScale : 1;
    var a = drawAlpha == null ? 1 : drawAlpha;
    var size = fallObjectSpriteSize(obj, pixelW, pixelH);
    var ghosts = layoutFallMotionBlurGhosts(px, py, size.width * s, size.height * s, {
      velocity: { x: vx, y: vy },
      intensity: intensity,
    });
    var origin = this._worldOrigin(opts);
    var id = body && body.id != null ? body.id : (obj.getType && obj.getType());
    for (var i = 0; i < ghosts.length; i++) {
      var g = ghosts[i];
      playfield.sprite({
        key: "fall-mb-" + id + "-" + i,
        image: img,
        x: origin.x + g.x,
        y: origin.y + g.y,
        width: g.width,
        height: g.height,
        rotation: angle || 0,
        alpha: a * g.alpha,
        zIndex: 14,
      });
    }
    return true;
  };

  FallRenderer.prototype.drawObjects = function (ctx, bodyEntries, runWidth, runHeight, currentTime, opts) {
    opts = opts || {};
    var animEnabled = C.FALL_SUCTION_ANIMATION_ENABLED != null
      ? C.FALL_SUCTION_ANIMATION_ENABLED : true;
    var suctionMs = C.FALL_SUCTION_ANIMATION_MS != null
      ? C.FALL_SUCTION_ANIMATION_MS : 50;
    var edgeHazard = { left: 0, right: 0 };

    for (var i = 0; i < bodyEntries.length; i++) {
      var entry = bodyEntries[i];
      var obj = entry.obj;
      var body = entry.body;
      if (!obj || !body) continue;
      if (obj.collected || obj.caught) continue;
      var type = obj.getType ? obj.getType() : "";

      // When animation is disabled, suctioned objects vanish immediately
      if (entry.suction && !animEnabled) continue;

      var px = body.position.x;
      var py = body.position.y;
      var drawWidthFrac = obj.width;
      var pixelW = fallObjectPixelWidth(drawWidthFrac, runWidth, this.runConfig);
      var aspect = obj.getAspectRatio ? obj.getAspectRatio() : 1;
      var pixelH = pixelW / (aspect > 0 ? aspect : 1);
      if (type === "bonus") {
        var telegraphPixelW = fallObjectPixelWidth(obj.width, runWidth, this.runConfig);
        if (opts.playfield && typeof opts.playfield.underFx === "function") {
          this._publishSpawnTelegraph(opts, type, px, py, telegraphPixelW, runHeight, currentTime);
        } else {
          this._drawSpawnTelegraph(ctx, type, px, py, telegraphPixelW, runHeight, currentTime);
        }
      }
      if (type === "boulder" && py < runHeight * 0.5) {
        var edgeBand = runWidth * 0.14;
        var yStrength = Math.max(0, 1 - (py / (runHeight * 0.5)));
        if (px < edgeBand) edgeHazard.left = Math.max(edgeHazard.left, yStrength);
        if (px > runWidth - edgeBand) edgeHazard.right = Math.max(edgeHazard.right, yStrength);
      }

      var willGpu = !!(opts.playfield && typeof opts.playfield.sprite === "function"
        && typeof obj.getDrawImage === "function" && obj.getDrawImage());
      if (this._shouldDrawMotionTrail(entry, currentTime, runHeight) && !willGpu) {
        this._drawObjectMotionTrail(ctx, entry, px, py, pixelW, pixelH, type, runHeight);
      }

      if (entry.suction) {
        // Fade-shrink animation: scale and alpha decrease over suction period
        var elapsed = currentTime - entry.suction.startTime;
        var t = Math.min(1, elapsed / suctionMs);
        var scale = 1 - t * 0.7;
        var alpha = 1 - t;
        if (alpha <= 0) continue;
        var angle = typeof body.angle === "number" ? body.angle : 0;

        var gpu = opts && opts.playfield && this._syncObjectSprite(
          opts.playfield,
          this._worldOrigin(opts),
          obj,
          body,
          px,
          py,
          pixelW,
          pixelH,
          alpha,
          scale,
          angle,
          15
        );
        if (gpu && this._shouldDrawMotionTrail(entry, currentTime, runHeight)) {
          this._publishObjectMotionTrail(opts, entry, px, py, pixelW, pixelH, alpha, scale, angle, runHeight);
        }
        if (!gpu) {
          ctx.save();
          ctx.globalAlpha = alpha;
          ctx.translate(px, py);
          if (Math.abs(angle) > 1e-4) ctx.rotate(angle);
          ctx.scale(scale, scale);
          obj.draw(ctx, -pixelW / 2, -pixelH / 2, pixelW, pixelH);
          ctx.restore();
        }
      } else {
        var angleLive = typeof body.angle === "number" ? body.angle : 0;
        // Spawn appear pop: scale-up (ease-out-back overshoot) + fade-in, like the
        // old big-flask item appear.
        var appearMs = this.runConfig.FALL_SPAWN_APPEAR_MS != null
          ? Number(this.runConfig.FALL_SPAWN_APPEAR_MS)
          : (C.RUN_CONFIG && C.RUN_CONFIG.FALL_SPAWN_APPEAR_MS != null
            ? Number(C.RUN_CONFIG.FALL_SPAWN_APPEAR_MS)
            : 260);
        var appScale = 1;
        var appAlpha = 1;
        if (entry.spawnTime != null && appearMs > 0 && currentTime != null) {
          var ap = (currentTime - entry.spawnTime) / appearMs;
          if (ap < 1) {
            var e = ap < 0 ? 0 : ap;
            var c1 = 1.70158;
            var c3 = c1 + 1;
            var back = 1 + c3 * Math.pow(e - 1, 3) + c1 * Math.pow(e - 1, 2);
            appScale = 0.4 + 0.6 * back;
            appAlpha = Math.min(1, e * 1.8);
          }
        }
        var gpuLive = opts && opts.playfield && this._syncObjectSprite(
          opts.playfield,
          this._worldOrigin(opts),
          obj,
          body,
          px,
          py,
          pixelW,
          pixelH,
          appAlpha,
          appScale,
          angleLive,
          15
        );
        if (gpuLive && this._shouldDrawMotionTrail(entry, currentTime, runHeight)) {
          this._publishObjectMotionTrail(
            opts, entry, px, py, pixelW, pixelH, appAlpha, appScale, angleLive, runHeight
          );
        }
        if (!gpuLive) {
          ctx.save();
          if (appAlpha < 1) ctx.globalAlpha = appAlpha;
          ctx.translate(px, py);
          if (Math.abs(angleLive) > 1e-4) ctx.rotate(angleLive);
          if (appScale !== 1) ctx.scale(appScale, appScale);
          obj.draw(ctx, -pixelW / 2, -pixelH / 2, pixelW, pixelH);
          ctx.restore();
        }
      }

      if (areRuntimeParticlesEnabled()
        && entry.spawnParticles
        && entry.spawnParticlesUntil != null
        && currentTime < entry.spawnParticlesUntil) {
        var spawnParticleMs = this.runConfig.FALL_SPAWN_PARTICLE_MS != null
          ? Number(this.runConfig.FALL_SPAWN_PARTICLE_MS)
          : (C.RUN_CONFIG && C.RUN_CONFIG.FALL_SPAWN_PARTICLE_MS != null
            ? Number(C.RUN_CONFIG.FALL_SPAWN_PARTICLE_MS)
            : 350);
        drawMergeParticles(
          ctx,
          entry.spawnParticles,
          entry.spawnParticlesStart || entry.spawnTime || 0,
          spawnParticleMs,
          currentTime,
          {
            playfield: opts.playfield,
            origin: this._worldOrigin(opts),
            keyPrefix: "spawn-" + (body.id != null ? body.id : i),
          }
        );
      }
    }
    this._drawEdgeHazardIndicators(ctx, runWidth, runHeight, edgeHazard, currentTime, opts);
  };

  FallRenderer.prototype._drawSpawnTelegraph = function (ctx, type, px, py, pixelW, runHeight, currentTime) {
    if (py > runHeight * 0.22) return;
    if (type !== "bonus") return;
    var pulse = 0.5 + 0.5 * Math.sin((currentTime || 0) * 0.02);
    var alpha = (0.18 + pulse * 0.22) * Math.max(0, 1 - (py / (runHeight * 0.22)));
    var radius = Math.max(12, pixelW * (0.55 + pulse * 0.2));
    var color = "167, 139, 250";
    ctx.save();
    ctx.strokeStyle = "rgba(" + color + "," + alpha.toFixed(3) + ")";
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.arc(px, py, radius, 0, Math.PI * 2);
    ctx.stroke();
    ctx.restore();
  };

  FallRenderer.prototype._publishSpawnTelegraph = function (opts, type, px, py, pixelW, runHeight, currentTime) {
    if (py > runHeight * 0.22) return;
    if (type !== "bonus") return;
    var playfield = opts && opts.playfield;
    if (!playfield || typeof playfield.underFx !== "function") return;
    var pulse = 0.5 + 0.5 * Math.sin((currentTime || 0) * 0.02);
    var alpha = (0.18 + pulse * 0.22) * Math.max(0, 1 - (py / (runHeight * 0.22)));
    var radius = Math.max(12, pixelW * (0.55 + pulse * 0.2));
    var origin = this._worldOrigin(opts);
    playfield.underFx({
      key: "telegraph-" + Math.round(px) + "-" + Math.round(py),
      kind: "ring",
      x: origin.x + px,
      y: origin.y + py,
      radius: radius,
      stroke: 2,
      rgb: [167, 139, 250],
      alpha: alpha,
      zIndex: 4,
    });
  };

  FallRenderer.prototype._drawEdgeHazardIndicators = function (ctx, runWidth, runHeight, edgeHazard, currentTime, opts) {
    var pulse = 0.6 + 0.4 * Math.sin((currentTime || 0) * 0.018);
    var top = runHeight * 0.2;
    var mid = runHeight * 0.35;
    var bottom = runHeight * 0.5;
    var playfield = opts && opts.playfield;
    var origin = this._worldOrigin(opts || {});
    function publishTri(key, points, alpha) {
      if (playfield && typeof playfield.effect === "function") {
        var pts = [];
        for (var i = 0; i < points.length; i += 2) {
          pts.push(points[i] + origin.x, points[i + 1] + origin.y);
        }
        playfield.effect({
          key: key,
          kind: "poly",
          points: pts,
          rgb: [239, 68, 68],
          alpha: alpha,
          zIndex: 25,
        });
        return true;
      }
      return false;
    }
    if (edgeHazard.left > 0) {
      var aL = 0.2 + edgeHazard.left * 0.45 * pulse;
      if (!publishTri("hazard-l", [0, mid, 18, top, 18, bottom], aL)) {
        ctx.save();
        ctx.fillStyle = "rgba(239, 68, 68," + aL.toFixed(3) + ")";
        ctx.beginPath();
        ctx.moveTo(0, mid);
        ctx.lineTo(18, top);
        ctx.lineTo(18, bottom);
        ctx.closePath();
        ctx.fill();
        ctx.restore();
      }
    }
    if (edgeHazard.right > 0) {
      var aR = 0.2 + edgeHazard.right * 0.45 * pulse;
      if (!publishTri("hazard-r", [runWidth, mid, runWidth - 18, top, runWidth - 18, bottom], aR)) {
        ctx.save();
        ctx.fillStyle = "rgba(239, 68, 68," + aR.toFixed(3) + ")";
        ctx.beginPath();
        ctx.moveTo(runWidth, mid);
        ctx.lineTo(runWidth - 18, top);
        ctx.lineTo(runWidth - 18, bottom);
        ctx.closePath();
        ctx.fill();
        ctx.restore();
      }
    }
  };

  FallRenderer.prototype._worldOrigin = function (opts) {
    opts = opts || {};
    return {
      x: opts.worldOrigin && opts.worldOrigin.x != null ? opts.worldOrigin.x : 0,
      y: opts.worldOrigin && opts.worldOrigin.y != null ? opts.worldOrigin.y : 0,
    };
  };

  FallRenderer.prototype._syncObjectSprite = function (playfield, origin, obj, body, px, py, pixelW, pixelH, alpha, scale, angle, zIndex) {
    if (!playfield || typeof playfield.sprite !== "function" || !obj) return false;
    var img = typeof obj.getDrawImage === "function" ? obj.getDrawImage() : null;
    if (!img) return false;
    var s = scale != null && isFinite(scale) ? scale : 1;
    var size = fallObjectSpriteSize(obj, pixelW, pixelH);
    playfield.sprite({
      key: "fall-" + (body && body.id != null ? body.id : obj.getType && obj.getType()),
      image: img,
      x: origin.x + px,
      y: origin.y + py,
      width: size.width * s,
      height: size.height * s,
      rotation: angle || 0,
      alpha: alpha == null ? 1 : alpha,
      zIndex: zIndex != null ? zIndex : 15,
    });
    return true;
  };

  /**
   * Sea buoys at the flask's horizontal stop points (left/right travel limits).
   */
  FallRenderer.prototype.drawMovementRange = function (ctx, opts) {
    if (!opts || !opts.showMovementRange) return;
    var dims = this._getFlaskDims(opts.runHeight);
    var playerX = opts.playerX;
    if (playerX == null && opts.player) playerX = opts.player.x;
    if (playerX == null) playerX = opts.runWidth / 2;
    var origin = this._worldOrigin(opts);
    var fallPhysics = opts.fallPhysics;
    if (opts.playfield) {
      var specs = collectFallBuoySpriteSpecs({
        runWidth: opts.runWidth,
        runHeight: opts.runHeight,
        playerX: playerX,
        scale: opts.scale || this.scale,
        scaleUi: this._getMockupScaleSide(),
        flaskDims: dims,
        currentTime: opts.animTime != null ? opts.animTime : opts.currentTime,
        stageIntroAlpha: opts.catcherFlaskIntroAlpha,
        originX: origin.x,
        originY: origin.y,
      });
      if (specs.length) {
        for (var i = 0; i < specs.length; i++) opts.playfield.sprite(specs[i]);
        return;
      }
    }
    drawFallMovementRange(ctx, {
      runWidth: opts.runWidth,
      runHeight: opts.runHeight,
      playerX: playerX,
      scale: opts.scale || this.scale,
      scaleUi: this._getMockupScaleSide(),
      flaskDims: dims,
      currentTime: opts.animTime != null ? opts.animTime : opts.currentTime,
      stageIntroAlpha: opts.catcherFlaskIntroAlpha,
      runConfig: this.runConfig,
      flaskGeometry: fallPhysics && fallPhysics.flaskGeometry ? fallPhysics.flaskGeometry : null,
    });
  };

  /**
   * drawFlask — player-controlled runway flask sprite.
   */
  FallRenderer.prototype.drawFlask = function (ctx, player, runWidth, runHeight, currentTime, introAlpha, opts) {
    if (!player || player.hp <= 0) return;
    var dims = this._getFlaskDims(runHeight);
    var flaskW = dims.flaskW;
    var flaskH = dims.flaskH;
    var flaskTopY = dims.flaskTopY;

    var flaskX = player.x - flaskW / 2;
    var flaskY = flaskTopY;
    var alpha = introAlpha != null && isFinite(introAlpha) ? Math.max(0, Math.min(1, introAlpha)) : 1;
    opts = opts || {};
    var playfield = opts.playfield;
    if (playfield && FlaskRenderer.collectFlaskVisualSprites) {
      var origin = this._worldOrigin(opts);
      var collected = FlaskRenderer.collectFlaskVisualSprites(
        origin.x + flaskX,
        origin.y + flaskY,
        flaskW,
        flaskH,
        player.hp,
        "catcher"
      );
      var specs = collected && collected.specs ? collected.specs : collected;
      if (specs && specs.length) {
        for (var i = 0; i < specs.length; i++) {
          specs[i].alpha = (specs[i].alpha == null ? 1 : specs[i].alpha) * alpha;
          playfield.sprite(specs[i]);
        }
        return;
      }
    }

    ctx.save();
    if (alpha < 1) ctx.globalAlpha = alpha;
    if (FlaskRenderer && FlaskRenderer.drawFlaskAtRect) {
      FlaskRenderer.drawFlaskAtRect(ctx, flaskX, flaskY, flaskW, flaskH, player.hp);
    }
    ctx.restore();
  };

  /**
   * Pointy-top hex outline centered at (x, y).
   */
  FallRenderer.prototype._traceHexCell = function (ctx, x, y, size) {
    ctx.beginPath();
    for (var i = 0; i < 6; i++) {
      var a = -Math.PI / 2 + i * (Math.PI / 3);
      var px = x + size * Math.cos(a);
      var py = y + size * Math.sin(a);
      if (i === 0) ctx.moveTo(px, py);
      else ctx.lineTo(px, py);
    }
    ctx.closePath();
  };

  /**
   * Subtle honeycomb stroke grid (caller must clip).
   */
  FallRenderer.prototype._strokeHexGrid = function (ctx, cx, cy, radius, hexSize) {
    if (!(hexSize > 1) || !(radius > 0)) return;
    var w = hexSize * Math.sqrt(3);
    var h = hexSize * 1.5;
    var minX = cx - radius - w;
    var maxX = cx + radius + w;
    var minY = cy - radius - h;
    var maxY = cy + radius + h;
    var row = 0;
    for (var y = minY; y <= maxY; y += h, row++) {
      var xOff = (row % 2) * (w * 0.5);
      for (var x = minX; x <= maxX; x += w) {
        this._traceHexCell(ctx, x + xOff, y, hexSize);
        ctx.stroke();
      }
    }
  };

  /**
   * drawShieldGlow — orange circular force-field with a subtle hex fill; materializes
   * (fade + scale-down) on pickup and dematerializes (fade + scale-up) on boulder break.
   */
  FallRenderer.prototype.drawShieldGlow = function (ctx, player, runWidth, runHeight, currentTime, opts) {
    if (!player || player.hp <= 0) return;
    var visual = typeof player.getShieldVisual === "function"
      ? player.getShieldVisual(currentTime)
      : (player.hasShield ? { alpha: 1, scale: 1 } : null);
    if (!visual || !(visual.alpha > 0.01)) return;

    var cfg = this.runConfig || {};
    var dims = this._getFlaskDims(runHeight);
    var flaskW = dims.flaskW;
    var flaskH = dims.flaskH;
    var flaskTopY = dims.flaskTopY;
    var flaskCx = player.x;
    var flaskCy = flaskTopY + flaskH / 2;
    var scale = visual.scale != null && isFinite(visual.scale) ? visual.scale : 1;
    var alpha = Math.max(0, Math.min(1, visual.alpha));
    var shieldR = Math.sqrt((flaskW / 2) * (flaskW / 2) + (flaskH / 2) * (flaskH / 2));
    var hexFrac = cfg.SHIELD_HEX_SIZE_FRAC != null ? Number(cfg.SHIELD_HEX_SIZE_FRAC) : 0.11;
    if (!(hexFrac > 0)) hexFrac = 0.11;
    var hexSize = Math.max(4, shieldR * hexFrac);
    var ringR = shieldR * 1.05;
    var playfield = opts && opts.playfield;
    if (playfield && typeof playfield.effect === "function") {
      var origin = this._worldOrigin(opts);
      var x = origin.x + flaskCx;
      var y = origin.y + flaskCy;
      var gpuR = shieldR * scale;
      var gpuRing = gpuR * 1.05;
      playfield.effect({
        key: "shield-glow",
        kind: "glow",
        blend: "add",
        x: x,
        y: y,
        width: gpuR * 2.7,
        height: gpuR * 2.7,
        rgb: [255, 140, 40],
        alpha: 0.35 * alpha,
        zIndex: 28,
      });
      playfield.effect({
        key: "shield-hex",
        kind: "hex-grid",
        x: x,
        y: y,
        clipRadius: gpuRing,
        hexSize: Math.max(4, gpuR * hexFrac),
        rgb: [255, 190, 100],
        stroke: Math.max(0.8, gpuR * 0.012),
        alpha: 0.28 * alpha,
        zIndex: 29,
      });
      playfield.effect({
        key: "shield-ring-a",
        kind: "ring",
        x: x,
        y: y,
        radius: gpuRing,
        stroke: 5,
        rgb: [255, 168, 64],
        alpha: 0.55 * alpha,
        zIndex: 30,
      });
      playfield.effect({
        key: "shield-ring-b",
        kind: "ring",
        x: x,
        y: y,
        radius: gpuRing,
        stroke: 3,
        rgb: [255, 210, 140],
        alpha: 0.95 * alpha,
        zIndex: 31,
      });
      return;
    }

    ctx.save();
    ctx.translate(flaskCx, flaskCy);
    ctx.scale(scale, scale);
    ctx.translate(-flaskCx, -flaskCy);
    ctx.globalAlpha = alpha;

    // Soft orange volume
    var g = ctx.createRadialGradient(
      flaskCx, flaskCy, shieldR * 0.2,
      flaskCx, flaskCy, shieldR * 1.35
    );
    g.addColorStop(0, "rgba(255, 150, 50, 0.22)");
    g.addColorStop(0.55, "rgba(255, 132, 36, 0.12)");
    g.addColorStop(1, "rgba(255, 120, 30, 0)");
    ctx.fillStyle = g;
    ctx.beginPath();
    ctx.arc(flaskCx, flaskCy, shieldR * 1.35, 0, Math.PI * 2);
    ctx.fill();

    // Hex pattern clipped to the ring radius (single grid; no nested half-size layer).
    var ringR = shieldR * 1.05;
    ctx.save();
    ctx.beginPath();
    ctx.arc(flaskCx, flaskCy, ringR, 0, Math.PI * 2);
    ctx.clip();
    ctx.strokeStyle = "rgba(255, 190, 100, 0.28)";
    ctx.lineWidth = Math.max(0.8, shieldR * 0.012);
    this._strokeHexGrid(ctx, flaskCx, flaskCy, ringR, hexSize);
    ctx.restore();

    // Orange ring
    ctx.strokeStyle = "rgba(255, 168, 64, 0.55)";
    ctx.lineWidth = 5;
    ctx.shadowColor = "rgba(255, 140, 40, 0.7)";
    ctx.shadowBlur = 12;
    ctx.beginPath();
    ctx.arc(flaskCx, flaskCy, ringR, 0, Math.PI * 2);
    ctx.stroke();

    ctx.strokeStyle = "rgba(255, 210, 140, 0.95)";
    ctx.lineWidth = 3;
    ctx.shadowBlur = 8;
    ctx.beginPath();
    ctx.arc(flaskCx, flaskCy, ringR, 0, Math.PI * 2);
    ctx.stroke();

    ctx.restore();
  };

  FallRenderer.prototype._getMockupScaleSide = function () {
    var fromDom = readMockupScaleSideFromDom();
    if (fromDom != null) return fromDom;
    if (this.mockupScale && this.mockupScale.scaleSide != null) {
      return this.mockupScale.scaleSide;
    }
    return 1;
  };

  FallRenderer.prototype._getCountdownFlaskHeightRatio = function () {
    var cfg = this.runConfig || {};
    var ratio = cfg.FALL_COUNTDOWN_FLASK_HEIGHT_RATIO != null
      ? Number(cfg.FALL_COUNTDOWN_FLASK_HEIGHT_RATIO)
      : (C.RUN_CONFIG && C.RUN_CONFIG.FALL_COUNTDOWN_FLASK_HEIGHT_RATIO != null
        ? Number(C.RUN_CONFIG.FALL_COUNTDOWN_FLASK_HEIGHT_RATIO)
        : 0.6);
    return isFinite(ratio) && ratio > 0 ? ratio : 0.6;
  };

  FallRenderer.prototype._getCountdownPopScaleMin = function () {
    var cfg = this.runConfig || {};
    var min = cfg.FALL_COUNTDOWN_POP_SCALE_MIN != null
      ? Number(cfg.FALL_COUNTDOWN_POP_SCALE_MIN)
      : (C.RUN_CONFIG && C.RUN_CONFIG.FALL_COUNTDOWN_POP_SCALE_MIN != null
        ? Number(C.RUN_CONFIG.FALL_COUNTDOWN_POP_SCALE_MIN)
        : 0.5);
    return isFinite(min) && min > 0 ? Math.min(1, min) : 0.5;
  };

  /** Canvas font family — quoted names match `core/game.js` (spaces break unquoted canvas fonts). */
  FallRenderer.prototype._countdownFontFamily = function () {
    return "'Baloo 2', 'Fredoka', sans-serif";
  };

  /** Peak digit size in play-column px — same flask height as `drawFlask`. */
  FallRenderer.prototype._getCountdownPeakFontSizePx = function (runHeight) {
    var scale = this.scale;
    var scaledFlaskH = scale && scale.runwayFlaskDisplayHeight != null
      ? Number(scale.runwayFlaskDisplayHeight)
      : NaN;
    if (isFinite(scaledFlaskH) && scaledFlaskH > 0) {
      var dims = this._getFlaskDims(runHeight || 600);
      var flaskH = dims && dims.flaskH > 0 ? dims.flaskH : scaledFlaskH;
      return Math.max(1, Math.round(flaskH * this._getCountdownFlaskHeightRatio()));
    }
    var cfg = this.runConfig || {};
    var basePx = cfg.FALL_COUNTDOWN_FONT_BASE_PX != null
      ? cfg.FALL_COUNTDOWN_FONT_BASE_PX
      : (C.RUN_CONFIG && C.RUN_CONFIG.FALL_COUNTDOWN_FONT_BASE_PX != null
        ? C.RUN_CONFIG.FALL_COUNTDOWN_FONT_BASE_PX
        : 72);
    return fallCountdownFontSizePx(this._getMockupScaleSide(), basePx);
  };

  /**
   * drawCountdown — animated digit over open playfield (no backdrop).
   * Pop/fade via `ctx.scale` + fixed peak font (pre-b1907b55); animating font-size alone
   * did not change on-screen glyph size in browsers.
   */
  FallRenderer.prototype.drawCountdown = function (ctx, runWidth, runHeight, countdownRemaining, opts) {
    if (!countdownRemaining || countdownRemaining <= 0) return;
    var displayNum = Math.ceil(countdownRemaining);
    if (displayNum <= 0) return;
    var secondElapsed = displayNum - countdownRemaining;
    var popT = Math.min(1, secondElapsed / 0.12);
    var popMin = this._getCountdownPopScaleMin();
    var popScale = popMin + popT * (1 - popMin);
    if (secondElapsed > 0.85) {
      popScale *= 1 - (secondElapsed - 0.85) / 0.15;
    }
    var alpha = secondElapsed < 0.08
      ? secondElapsed / 0.08
      : (secondElapsed > 0.85 ? 1 - (secondElapsed - 0.85) / 0.15 : 1);
    alpha = Math.max(0, Math.min(1, alpha));
    popScale = Math.max(0, popScale);
    var peakFontPx = this._getCountdownPeakFontSizePx(runHeight);
    var playfield = opts && opts.playfield;
    if (playfield && typeof playfield.effect === "function") {
      var origin = this._worldOrigin(opts);
      playfield.effect({
        key: "countdown",
        kind: "label",
        x: origin.x + runWidth / 2,
        y: origin.y + runHeight * 0.38,
        text: String(displayNum),
        fontSize: peakFontPx,
        fontWeight: "700",
        fill: 0x1b5e7d,
        scale: popScale,
        alpha: alpha,
        zIndex: 50,
      });
      return;
    }

    ctx.save();
    ctx.globalAlpha = 1;
    ctx.translate(runWidth / 2, runHeight * 0.38);
    ctx.scale(popScale, popScale);
    ctx.font = "bold " + peakFontPx + "px " + this._countdownFontFamily();
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillStyle = "rgba(27, 94, 125, " + alpha.toFixed(3) + ")";
    ctx.fillText(String(displayNum), 0, 0);
    ctx.restore();
  };

  /**
   * One centered line at the countdown anchor. Score-popup face (white fill, teal stroke).
   * @param {{ text?: string, ageMs?: number, durationMs?: number }|null} callout
   */
  FallRenderer.prototype.drawCallout = function (ctx, runWidth, runHeight, callout, opts) {
    if (!callout || !callout.text) return;
    var reduced = prefersReducedMotion();
    var look = getFallCalloutPresentation(callout.ageMs, callout.durationMs, reduced);
    if (!look || look.alpha <= 0) return;
    var peakFontPx = computeFallCalloutFontPx(
      runWidth,
      this._getCountdownPeakFontSizePx(runHeight)
    );
    var playfield = opts && opts.playfield;
    var y = runHeight * FALL_CALLOUT_ANCHOR_Y;
    if (playfield && typeof playfield.effect === "function") {
      var origin = this._worldOrigin(opts);
      playfield.effect({
        key: "fall-callout",
        kind: "label",
        x: origin.x + runWidth / 2,
        y: origin.y + y + (look.offsetY || 0),
        text: String(callout.text),
        fontSize: peakFontPx,
        fontWeight: "700",
        fill: 0xffffff,
        strokeFill: 0x1b5e7d,
        stroke: computeScorePopupStrokeWidth(peakFontPx),
        scale: look.scale,
        scaleX: look.scaleX,
        scaleY: look.scaleY,
        alpha: look.alpha,
        zIndex: 51,
      });
      return;
    }
    if (!ctx || typeof ctx.save !== "function") return;
    var alphaStr = look.alpha.toFixed(3);
    var scaleX = look.scaleX != null ? look.scaleX : look.scale;
    var scaleY = look.scaleY != null ? look.scaleY : look.scale;
    var offsetY = look.offsetY || 0;
    ctx.save();
    ctx.translate(runWidth / 2, y + offsetY);
    ctx.scale(scaleX, scaleY);
    ctx.font = "700 " + peakFontPx + "px " + this._countdownFontFamily();
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.lineJoin = "round";
    ctx.lineWidth = computeScorePopupStrokeWidth(peakFontPx);
    ctx.strokeStyle = "rgba(27, 94, 125, " + alphaStr + ")";
    ctx.fillStyle = "rgba(255, 255, 255, " + alphaStr + ")";
    if (typeof ctx.strokeText === "function") ctx.strokeText(String(callout.text), 0, 0);
    ctx.fillText(String(callout.text), 0, 0);
    ctx.restore();
  };

  /**
   * Play logic stays in runWidth×runHeight; draw clip extends so spawns, side
   * bounces, and the runway flask at column edges stay visible until the sea.
   */
  FallRenderer.prototype.getDrawClipRect = function (opts) {
    opts = opts || {};
    var runWidth = Math.max(1, Number(opts.runWidth) || 1);
    var runHeight = Math.max(1, Number(opts.runHeight) || 1);
    var cfg = this.runConfig || {};
    var topOverflow = opts.clipTopOverflow != null
      ? Math.max(0, Number(opts.clipTopOverflow))
      : computeFallDrawTopOverflowPx(runWidth, cfg, runHeight);
    var bottomLocal = opts.clipBottomLocal != null && isFinite(Number(opts.clipBottomLocal))
      ? Math.max(runHeight, Number(opts.clipBottomLocal))
      : runHeight;
    var clipX = 0;
    var clipW = runWidth;
    var originX = opts.clipOriginX != null ? Number(opts.clipOriginX) : 0;
    var canvasW = opts.clipCanvasWidth != null ? Number(opts.clipCanvasWidth) : 0;
    if (isFinite(canvasW) && canvasW > 0) {
      clipX = -Math.max(0, originX);
      clipW = canvasW;
    } else {
      var scale = opts.scale || this.scale;
      var flaskW = scale && scale.runwayFlaskDisplayWidth ? scale.runwayFlaskDisplayWidth : 120;
      var sidePad = Math.max(flaskW, runWidth * 0.35);
      clipX = -sidePad;
      clipW = runWidth + sidePad * 2;
    }
    return {
      x: clipX,
      y: -topOverflow,
      width: clipW,
      height: bottomLocal + topOverflow,
    };
  };

  /**
   * draw — full frame render. Called each game loop step.
   */
  FallRenderer.prototype.draw = function (ctx, opts) {
    if (opts.scale) this.scale = opts.scale;
    var runWidth = opts.runWidth;
    var runHeight = opts.runHeight;
    var bodyEntries = opts.bodyEntries || [];
    var player = opts.player;
    var currentTime = opts.currentTime || 0;
    var state = opts.state || "playing";
    var countdownRemaining = opts.countdownRemaining || 0;
    ctx.save();
    var clip = this.getDrawClipRect(opts);
    ctx.beginPath();
    ctx.rect(clip.x, clip.y, clip.width, clip.height);
    ctx.clip();

    var catcherIntroAlpha = opts.catcherFlaskIntroAlpha != null ? opts.catcherFlaskIntroAlpha : 1;
    if (opts.playfield) {
      var origin = this._worldOrigin(opts);
      opts.playfield.setWorldClip(
        origin.x + clip.x,
        origin.y + clip.y,
        clip.width,
        clip.height
      );
    }
    if (opts.showMovementRange) {
      this.drawMovementRange(ctx, {
        showMovementRange: true,
        runWidth: runWidth,
        runHeight: runHeight,
        player: player,
        playerX: player ? player.x : runWidth / 2,
        scale: opts.scale || this.scale,
        currentTime: currentTime,
        animTime: opts.animTime,
        catcherFlaskIntroAlpha: catcherIntroAlpha,
        playfield: opts.playfield,
        worldOrigin: opts.worldOrigin,
        fallPhysics: opts.fallPhysics,
      });
    }
    if (player) {
      this.drawFlask(ctx, player, runWidth, runHeight, currentTime, catcherIntroAlpha, opts);
    }
    if (opts.fallPhysics) {
      drawFallColliderDebug(ctx, opts.fallPhysics, { bodyEntries: bodyEntries });
    }
    this.drawObjects(ctx, bodyEntries, runWidth, runHeight, currentTime, opts);
    if (player) {
      this.drawShieldGlow(
        ctx,
        player,
        runWidth,
        runHeight,
        opts.animTime != null ? opts.animTime : currentTime,
        opts
      );
    }
    if (opts.flaskShardBursts && opts.flaskShardBursts.length && FlaskRenderer.drawFlaskShardBursts) {
      FlaskRenderer.drawFlaskShardBursts(
        ctx,
        opts.flaskShardBursts,
        opts.animTime != null ? opts.animTime : currentTime,
        {
          gravityPx: opts.flaskShardGravityPx,
          viewport: opts.flaskShardViewport,
          playfield: opts.playfield,
          worldOrigin: opts.worldOrigin,
        }
      );
    }

    var cfg = this.runConfig || {};
    var silentCountdown = !!cfg.fallCountdownSilent
      || opts.fallCountdownSilent === true;
    if (state === "countdown" && countdownRemaining > 0 && !silentCountdown) {
      this.drawCountdown(ctx, runWidth, runHeight, countdownRemaining, opts);
    }
    if (opts.fallCallout) {
      this.drawCallout(ctx, runWidth, runHeight, opts.fallCallout, opts);
    }

    ctx.restore();
  };